// Making a small picture bigger by inventing the detail.
//
// Real-ESRGAN in its compact form: 4,7 MB, the same weight as the fast
// background-removal model, and BSD-3 licensed. It always produces four times
// the width and four times the height; anything else is that result resampled
// down afterwards.
//
// The reason this tool has a size limit and the others do not is the shape of
// the work. The background remover looks at a fixed 320x320 copy however large
// the picture is. This runs over every input pixel and writes sixteen, so the
// cost grows with the picture, and it grows fast: measured on one core through
// WebAssembly it manages roughly fourteen thousand input pixels a second. A
// 512 pixel square is about twenty seconds. A full HD frame would be two and a
// half minutes and would need a four hundred megabyte tensor to hold the
// answer, which is why MAX_INPUT_EDGE exists and is not negotiable at runtime.

import { sizeOf, hasAlpha } from './images.js';
import { loadRuntime, sessionFor } from './onnx.js';

export const MODEL = {
    id: 'upscale-general-x4',
    file: '/models/realesr-general-x4v3.onnx',
    label: 'Upscaler',
    bytes: 4871181,
};

/** What the network multiplies both edges by. Fixed by the weights. */
export const SCALE = 4;

/** The largest picture this will accept, measured on its longest edge. */
export const MAX_INPUT_EDGE = 512;

// Rows of the picture handled per pass. Three passes for a picture at the size
// limit, which is enough for the progress to mean something, and one pass with
// no overhead at all for the small pictures this is mostly used on.
const STRIP_ROWS = 192;

// Rows carried into the pass on each side of a strip and thrown away again.
//
// Without it a strip has no idea what sits just outside it, and the seam is
// glaring: measured against a single pass over the whole picture, a strip with
// no padding is out by 158 of 255 at the join. Padding is what buys that back,
// and the numbers say exactly how much is needed. Against a checkerboard, which
// is the worst case for a network that invents detail:
//
//     pad  8   out by 14 of 255
//     pad 16   out by  3
//     pad 24   out by  0.5
//     pad 32   out by  0.000, identical
//
// Thirty-two is where the network stops being able to see any further, so the
// result is the same image a single pass would have produced rather than a
// close one. Real-ESRGAN's own tiler settles for ten and a visible seam. The
// cost here is a quarter more work at the size limit and none below it.
const STRIP_PAD = 32;

/** A canvas holding the picture, ready to be read a strip at a time. */
function toCanvas(image, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, width, height);
    return canvas;
}

/** One strip of pixels as the tensor the network expects: 1x3xHxW, 0 to 1. */
function toTensor(ort, imageData, width, height) {
    const pixels = width * height;
    const input = new Float32Array(3 * pixels);
    const { data } = imageData;

    // Channel first, and no mean or standard deviation: this network was
    // trained on plain 0 to 1 values, unlike the segmentation one next door.
    for (let p = 0; p < pixels; p += 1) {
        const source = p * 4;
        input[p] = data[source] / 255;
        input[pixels + p] = data[source + 1] / 255;
        input[2 * pixels + p] = data[source + 2] / 255;
    }

    return new ort.Tensor('float32', input, [1, 3, height, width]);
}

/** The network's answer, written into a canvas at the given offset. */
function paint(ctx, tensor, width, height, offsetY, skipTop, keepRows) {
    const pixels = width * height;
    const out = ctx.createImageData(width, keepRows);
    const values = tensor.data;

    for (let y = 0; y < keepRows; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const from = (y + skipTop) * width + x;
            const to = (y * width + x) * 4;
            // The network can answer slightly outside 0 to 1, so this clamps
            // rather than trusting it; a value of 1.02 would wrap to black.
            out.data[to] = Math.max(0, Math.min(255, values[from] * 255));
            out.data[to + 1] = Math.max(0, Math.min(255, values[pixels + from] * 255));
            out.data[to + 2] = Math.max(0, Math.min(255, values[2 * pixels + from] * 255));
            out.data[to + 3] = 255;
        }
    }

    ctx.putImageData(out, 0, offsetY);
}

/**
 * Carry the transparency across, since the network cannot.
 *
 * Real-ESRGAN works on three channels and has no idea an alpha channel exists,
 * so a cut-out would come back with its holes filled in. That matters here more
 * than it would elsewhere: the background remover next door produces exactly
 * such pictures, and the two are an obvious pair to use one after the other.
 *
 * The alpha is enlarged by plain resampling rather than by the network. There
 * is nothing for a network to invent in a coverage mask, and a smooth edge is
 * what a smooth edge should stay.
 */
function restoreAlpha(canvas, image, width, height) {
    const mask = document.createElement('canvas');
    mask.width = canvas.width;
    mask.height = canvas.height;

    const maskCtx = mask.getContext('2d', { willReadFrequently: true });
    maskCtx.imageSmoothingEnabled = true;
    maskCtx.imageSmoothingQuality = 'high';
    maskCtx.drawImage(image, 0, 0, width, height, 0, 0, canvas.width, canvas.height);

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const result = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const source = maskCtx.getImageData(0, 0, canvas.width, canvas.height);

    for (let i = 3; i < result.data.length; i += 4) {
        result.data[i] = source.data[i];
    }

    ctx.putImageData(result, 0, 0);
}

/**
 * Whether a picture is small enough to be worth doing this to.
 *
 * Returns the reason it is not, or null when it is, because the interface has
 * to explain the refusal rather than just disable a button.
 */
export function tooLargeReason(image) {
    const { width, height } = sizeOf(image);
    const longest = Math.max(width, height);
    if (longest <= MAX_INPUT_EDGE) return null;

    return `This picture is ${width} by ${height}. Upscaling is for pictures that are `
        + `too small, and there is nothing here to invent. The limit is ${MAX_INPUT_EDGE} `
        + `pixels on the longest edge, because the work grows with the picture: this one `
        + `would take about ${Math.round((width * height) / 14000)} seconds and more memory `
        + `than a browser tab will give.`;
}

/**
 * Run the upscaler.
 *
 * @param {ImageBitmap|HTMLImageElement} image
 * @param {object}   options
 * @param {Function} [options.onDownload] Model download progress, 0 to 1.
 * @param {Function} [options.onStage]    A short status string.
 * @param {Function} [options.onProgress] Strips finished, 0 to 1.
 * @returns {Promise<HTMLCanvasElement>} Four times the width and height.
 */
export async function upscale(image, { onDownload, onStage, onProgress } = {}) {
    const reason = tooLargeReason(image);
    if (reason) throw new Error(reason);

    if (onStage) onStage('Preparing the model');
    const session = await sessionFor(MODEL, onDownload);
    const ort = await loadRuntime();

    const { width, height } = sizeOf(image);
    const source = toCanvas(image, width, height);
    const sourceCtx = source.getContext('2d', { willReadFrequently: true });

    const out = document.createElement('canvas');
    out.width = width * SCALE;
    out.height = height * SCALE;
    const outCtx = out.getContext('2d');

    const strips = Math.ceil(height / STRIP_ROWS);
    for (let i = 0; i < strips; i += 1) {
        const top = i * STRIP_ROWS;
        const rows = Math.min(STRIP_ROWS, height - top);

        // The padding is only what is actually there: the first strip has
        // nothing above it and the last nothing below.
        const padTop = Math.min(STRIP_PAD, top);
        const padBottom = Math.min(STRIP_PAD, height - (top + rows));

        const readTop = top - padTop;
        const readRows = rows + padTop + padBottom;

        if (onStage) onStage(strips > 1 ? `Working, strip ${i + 1} of ${strips}` : 'Working');

        const data = sourceCtx.getImageData(0, readTop, width, readRows);
        const feeds = { [session.inputNames[0]]: toTensor(ort, data, width, readRows) };
        const result = await session.run(feeds);

        paint(
            outCtx,
            result[session.outputNames[0]],
            width * SCALE,
            readRows * SCALE,
            top * SCALE,
            padTop * SCALE,
            rows * SCALE
        );

        if (onProgress) onProgress((i + 1) / strips);
    }

    if (hasAlpha(image)) {
        if (onStage) onStage('Putting the transparency back');
        restoreAlpha(out, image, width, height);
    }

    return out;
}

/** The result at a different multiple, resampled down from the four times one. */
export function atScale(canvas, scale) {
    if (scale >= SCALE) return canvas;

    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round((canvas.width / SCALE) * scale));
    out.height = Math.max(1, Math.round((canvas.height / SCALE) * scale));

    const ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return out;
}
